import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions'
import type { FeedbackClientContext, FeedbackType } from '@/lib/feedbackContext'
import type { FeedbackSentiment, LlmFeedbackReason } from '@/types/llmFeedback'
import type { Budget, Lightness, Novelty, PlanScheduling, RecipeMix, WeekEffort } from '@/types/mealPlan'
import { app } from './config'

export const functions = getFunctions(app)

if (import.meta.env.DEV) {
  try {
    connectFunctionsEmulator(functions, 'localhost', 5010)
  } catch (_error) {
    // Already connected
  }
}

export function callSendPasswordResetEmail(email: string): Promise<{ data: { success: boolean } }> {
  const fn = httpsCallable<{ email: string }, { success: boolean }>(functions, 'sendPasswordResetEmail')
  return fn({ email })
}

export interface SubmitMealPlanRequestInput {
  daysRequested: number
  servings: number
  levelUpsEnabled: boolean
  // Per-plan levers from the build-a-plan questions. OMIT rather than pass `undefined` — callables
  // serialize undefined to null, which the backend rejects.
  // The busy-week answer as chosen, Normal included. Absent means the question was skipped.
  weekEffort?: WeekEffort
  recipeMix?: RecipeMix
  // Which food profiles govern THIS plan, when the user changed the selection. Absent means their
  // saved profiles apply — the safe default, since an empty array means no rules at all.
  activeProfileIds?: string[]
  // The week's mood. Flat here; the backend groups it into PlanRequest.planMood. Each is sent when
  // the user answered that question, INCLUDING a neutral answer.
  lightness?: Lightness
  novelty?: Novelty
  budget?: Budget
}

export interface SubmitMealPlanRequestResult {
  success: boolean
  requestId: string
  // The `meal_plan_results` doc is keyed by `planId`, NOT `requestId`. The callable mints both ids in one
  // transaction, so the FE knows both paths the instant this resolves.
  planId: string
}

export function callSubmitMealPlanRequest(input: SubmitMealPlanRequestInput): Promise<{ data: SubmitMealPlanRequestResult }> {
  const fn = httpsCallable<SubmitMealPlanRequestInput, SubmitMealPlanRequestResult>(functions, 'submitMealPlanRequest')
  return fn(input)
}

// addMealToPlan — append existing library meals to a draft/active plan. The plan's slots and cooks are
// backend-only by rules, so this is the one mutation path; a whole selection lands in ONE transaction.
export interface AddedDinner {
  mealId: string
  cookId: string
  slotIndex: number
}

export interface AddMealToPlanCallResult {
  success: boolean
  added: AddedDinner[]
  // Pre-batch response fields, echoing added[0]. Kept only for callers loaded before
  // the batch deploy; new code reads `added`.
  cookId: string
  slotIndex: number
}

export function callAddMealsToPlan(planId: string, mealIds: string[]): Promise<{ data: AddMealToPlanCallResult }> {
  const fn = httpsCallable<{ planId: string; mealIds: string[] }, AddMealToPlanCallResult>(functions, 'addMealToPlan')
  return fn({ planId, mealIds })
}

export function callAddMealToPlan(planId: string, mealId: string): Promise<{ data: AddMealToPlanCallResult }> {
  return callAddMealsToPlan(planId, [mealId])
}

// Copies a past plan's dinners into a new draft and returns its id.
export function callPlanAgain(sourcePlanId: string): Promise<{ data: { planId: string } }> {
  const fn = httpsCallable<{ sourcePlanId: string }, { planId: string }>(functions, 'planAgain')
  return fn({ sourcePlanId })
}

// removeMealsFromPlan — the inverse. Addressed by COOK id, not meal: a plan can hold the same dish on
// two nights, so a mealId wouldn't say which one to drop.
/**
 * One removed dinner, opaque to the client — hand it back to `callRestoreDinnersToPlan` to restore it to
 * its original day, under its original id, with its notes. The shape is the server's; treat it as a memento.
 */
export interface RemovedDinner {
  slot: { cookId: string | null; mealId: string | null }
  index: number
  cookState?: unknown
}

export interface RemoveMealsFromPlanCallResult {
  success: boolean
  removed: RemovedDinner[]
  remainingSlots: number
}

/**
 * Why a removal was refused, from the thrown error's `details.reason` — one HTTPS code covers several
 * refusals, and only some are worth retrying. Mirrors `RemoveMealsFromPlanRejection` in functions.
 */
export enum RemoveDinnerRefusal {
  PlanNotFound = 'planNotFound',
  PlanNotOpen = 'planNotOpen',
  PlanDeleted = 'planDeleted',
  CookNotInPlan = 'cookNotInPlan',
  CookAlreadyCooked = 'cookAlreadyCooked'
}

export function removeRefusalOf(error: unknown): RemoveDinnerRefusal | undefined {
  const reason = (error as { details?: { reason?: unknown } })?.details?.reason
  return typeof reason === 'string' && (Object.values(RemoveDinnerRefusal) as string[]).includes(reason)
    ? (reason as RemoveDinnerRefusal)
    : undefined
}

export function callRemoveMealsFromPlan(planId: string, cookIds: string[]): Promise<{ data: RemoveMealsFromPlanCallResult }> {
  const fn = httpsCallable<{ planId: string; cookIds: string[] }, RemoveMealsFromPlanCallResult>(functions, 'removeMealsFromPlan')
  return fn({ planId, cookIds })
}

export function callRestoreDinnersToPlan(
  planId: string,
  dinners: RemovedDinner[]
): Promise<{ data: { success: boolean; restored: string[] } }> {
  const fn = httpsCallable<{ planId: string; dinners: RemovedDinner[] }, { success: boolean; restored: string[] }>(
    functions,
    'restoreDinnersToPlan'
  )
  return fn({ planId, dinners })
}

// reorderPlanSlots — persist the plan's cooking order. Send a WHOLE order, not a move: the caller
// debounces a burst of drags, so a delta would be ambiguous. Omitted slots keep their day rather than
// being swept to the end. `changed` is false when the order already matched and nothing was written.
export function callReorderPlanSlots(planId: string, cookIds: string[]): Promise<{ data: { success: boolean; changed: boolean } }> {
  const fn = httpsCallable<{ planId: string; cookIds: string[] }, { success: boolean; changed: boolean }>(functions, 'reorderPlanSlots')
  return fn({ planId, cookIds })
}

// setPlanSlotDay — move one dinner to a given day of the plan, counted from its start. A day OF
// THE PLAN rather than a calendar date, so the plan keeps its shape when the start date moves.
// The write re-sorts, so the caller has nothing to reorder afterwards.
export function callSetPlanSlotDay(
  planId: string,
  cookId: string,
  dayOffset: number
): Promise<{ data: { success: boolean; changed: boolean } }> {
  const fn = httpsCallable<{ planId: string; cookId: string; dayOffset: number }, { success: boolean; changed: boolean }>(
    functions,
    'setPlanSlotDay'
  )
  return fn({ planId, cookId, dayOffset })
}

// setPlanScheduling — switch a plan between scheduled and flexible. A callable, not a plain
// field write: entering scheduled mode may drop pins the order no longer supports, and those
// live in the engine-owned plan blob.
export function callSetPlanScheduling(
  planId: string,
  scheduling: PlanScheduling
): Promise<{ data: { success: boolean; changed: boolean } }> {
  const fn = httpsCallable<{ planId: string; scheduling: PlanScheduling }, { success: boolean; changed: boolean }>(
    functions,
    'setPlanScheduling'
  )
  return fn({ planId, scheduling })
}

// Beta invites — read-only gate validation. unknown/revoked/malformed all
// collapse to reason 'invalid'.
export interface CheckInviteResult {
  valid: boolean
  reason?: 'invalid' | 'used' | 'expired'
}

export function callCheckInvite(code: string): Promise<{ data: CheckInviteResult }> {
  const fn = httpsCallable<{ code: string }, CheckInviteResult>(functions, 'checkInvite')
  return fn({ code })
}

// claimInvite — redeem the code after sign-in; creates the profile doc. On
// failure it throws a FunctionsError whose `details.reason` is the ClaimFailureReason
// (unknown / used / revoked / expired / already_claimed).
export interface ClaimInviteResult {
  success: boolean
  outcome: 'redeemed' | 'already_redeemed'
}

export function callClaimInvite(code: string): Promise<{ data: ClaimInviteResult }> {
  const fn = httpsCallable<{ code: string }, ClaimInviteResult>(functions, 'claimInvite')
  return fn({ code })
}

// mintAndSendInvite — an inviter-capable user mints one invite and emails it to a
// friend, bounded by their per-window quota. On failure it throws a FunctionsError whose
// `code` is e.g. 'functions/permission-denied' (no invite access) or
// 'functions/resource-exhausted' (out of invites), with `details.reason` carrying the
// machine-readable cause. `remaining` is the authoritative count left after this send.
export interface MintAndSendInviteResult {
  success: boolean
  sentTo: string
  remaining: number
}

export function callMintAndSendInvite(email: string): Promise<{ data: MintAndSendInviteResult }> {
  const fn = httpsCallable<{ email: string }, MintAndSendInviteResult>(functions, 'mintAndSendInvite')
  return fn({ email })
}

// mintQrInvite — the "skip email" sibling of mintAndSendInvite: mint one UNASSIGNED invite and get the
// code + signup URL back. Same per-window quota, consumed on mint. `remaining` is authoritative after.
export interface MintQrInviteResult {
  success: boolean
  code: string
  inviteUrl: string
  remaining: number
}

export function callMintQrInvite(): Promise<{ data: MintQrInviteResult }> {
  const fn = httpsCallable<Record<string, never>, MintQrInviteResult>(functions, 'mintQrInvite')
  return fn({})
}

// submitFeedback — any signed-in user files a bug or leaves feedback. Identity is
// stamped server-side from the auth token; the client sends only the message (+ optional
// `expected` for bugs) and best-effort diagnostic context. Returns the new report id.
export interface SubmitFeedbackInput {
  type: FeedbackType
  message: string
  expected?: string
  // Reply-to address the reporter chose — pre-filled from their account email but editable.
  // Omit (don't pass an empty string) to fall back to the server-stamped identity. The
  // confirmation email that will use this is a follow-up.
  contactEmail?: string
  clientContext?: FeedbackClientContext
}

export interface SubmitFeedbackResult {
  success: boolean
  id: string
}

export function callSubmitFeedback(input: SubmitFeedbackInput): Promise<{ data: SubmitFeedbackResult }> {
  const fn = httpsCallable<SubmitFeedbackInput, SubmitFeedbackResult>(functions, 'submitFeedback')
  return fn(input)
}

// submitLlmFeedback — rate a piece of generated content. The client names the generation
// and its verdict; the server stamps identity from the auth token and copies the call,
// prompt version and model off the generation, so a rating can't be misattributed or
// mislabelled. Upserts by `{generationId}_{uid}`, so re-rating replaces rather than stacks.
// A `null` sentiment clears an existing rating.
export interface SubmitLlmFeedbackInput {
  generationId: string
  sentiment: FeedbackSentiment | null
  reasons?: LlmFeedbackReason[]
  comment?: string
  appVersion?: string
  gitSha?: string
  locale?: string
}

export function callSubmitLlmFeedback(input: SubmitLlmFeedbackInput): Promise<{ data: { success: boolean } }> {
  const fn = httpsCallable<SubmitLlmFeedbackInput, { success: boolean }>(functions, 'submitLlmFeedback')
  return fn(input)
}

// Conversion — paused or trialing → card on file → resumed. Two calls with a
// hosted Stripe Checkout redirect between them. `@stripe/stripe-js` is deliberately NOT a
// dependency: the client only ever follows a URL, so no card data touches Parsley's origin.

/**
 * Why a conversion couldn't be attempted. A declined card is NOT here — see {@link ConversionOutcome}.
 * Two of these share `failed-precondition`, which is exactly why the reason travels separately.
 */
export enum ConversionRefusal {
  NoSubscription = 'noSubscription',
  NotConvertible = 'notConvertible',
  SetupIncomplete = 'setupIncomplete',
  SessionMismatch = 'sessionMismatch'
}

/**
 * How a conversion landed — all successful calls, differing in what to tell the user. `ResumeDeclined`
 * must read as "we couldn't charge that card", never as a failed conversion.
 */
export enum ConversionOutcome {
  Resumed = 'resumed',
  ResumeDeclined = 'resumeDeclined',
  CardSaved = 'cardSaved',
  AlreadyActive = 'alreadyActive',
  /** past_due → active: the outstanding invoice was settled with the new card. */
  Recovered = 'recovered',
  /** past_due → still past_due: the new card was declined too, grace keeps running. */
  RecoveryDeclined = 'recoveryDeclined',
  /** canceled → active: a NEW subscription, since an ended one can't be revived. */
  Restarted = 'restarted',
  /** canceled → still canceled: the new subscription's first charge was declined. */
  RestartDeclined = 'restartDeclined'
}

export interface ConversionResult {
  outcome: ConversionOutcome
  status: string
  tier: string
}

export function conversionRefusalOf(error: unknown): ConversionRefusal | undefined {
  const reason = (error as { details?: { reason?: unknown } })?.details?.reason
  return typeof reason === 'string' && (Object.values(ConversionRefusal) as string[]).includes(reason)
    ? (reason as ConversionRefusal)
    : undefined
}

/**
 * Returns a hosted Checkout URL to navigate to; the tier is recorded server-side on the session.
 * Omit `tier` for a past_due card replacement — that account already has a plan and a price.
 */
export function callCreateSetupCheckoutSession(tier?: string): Promise<{ data: { url: string } }> {
  const fn = httpsCallable<{ tier?: string }, { url: string }>(functions, 'createSetupCheckoutSession')
  // Omit rather than send undefined — Firebase callables serialise undefined to null, which the
  // validator would then have to special-case.
  return fn(tier ? { tier } : {})
}

/**
 * Finish the round trip. Idempotent — the return URL gets reloaded and replayed. The outcome decides what
 * to SAY; the UI learns the conversion worked from the billing snapshot the webhook writes.
 */
export function callCompleteConversion(sessionId: string): Promise<{ data: ConversionResult }> {
  const fn = httpsCallable<{ sessionId: string }, ConversionResult>(functions, 'completeConversion')
  return fn({ sessionId })
}

/**
 * Ask for this account to be deleted. One call: the server records the request and then cancels the
 * subscription and deletes the auth user itself, retrying until both are done — so nothing can
 * half-happen if this client goes away.
 *
 * Refuses with `failed-precondition` / `reason: 'staleAuth'` unless the caller re-authenticated in
 * the last five minutes.
 */
export function callRequestAccountDeletion(): Promise<{ data: { alreadyRequested: boolean; feedbackToken: string | null } }> {
  const fn = httpsCallable<Record<string, never>, { alreadyRequested: boolean; feedbackToken: string | null }>(
    functions,
    'requestAccountDeletion'
  )
  return fn({})
}

/**
 * Record why someone left, once their account is gone. Takes no auth — by the time this is answered
 * there is no session — so the one-time token minted with the deletion request is what authorises it.
 */
export function callSubmitDeletionFeedback(input: {
  token: string
  reason: string | null
  note: string
}): Promise<{ data: { recorded: boolean } }> {
  const fn = httpsCallable<typeof input, { recorded: boolean }>(functions, 'submitDeletionFeedback')
  return fn(input)
}

/**
 * Open Stripe's Customer Portal — card changes, cancellation, plan switching, invoices. The only route
 * back for a canceled subscription: restarting is a `create`, which `/subscribe` can't serve.
 */
export function callCreatePortalSession(): Promise<{ data: { url: string } }> {
  const fn = httpsCallable<Record<string, never>, { url: string }>(functions, 'createPortalSession')
  return fn({})
}

/**
 * The Stripe-hosted page for an unpaid invoice — where a 3D Secure challenge gets answered. Fetched
 * per click rather than stored: the URL expires after 30 days and voiding the invoice kills it.
 */
export function callGetOutstandingInvoiceUrl(): Promise<{ data: { url: string } }> {
  const fn = httpsCallable<Record<string, never>, { url: string }>(functions, 'getOutstandingInvoiceUrl')
  return fn({})
}

/**
 * Call off a cancellation that hasn't happened yet. Not `resume` — that un-pauses a paused
 * subscription; this clears `cancel_at_period_end` on one still live.
 */
export function callUndoScheduledCancellation(): Promise<{ data: { resumed: boolean } }> {
  const fn = httpsCallable<Record<string, never>, { resumed: boolean }>(functions, 'undoScheduledCancellation')
  return fn({})
}
